/**
 * Jump-nav centering — shared primitive.
 *
 * The mobile "jump nav" (`.js-jump-nav`) is a horizontally scrolling row of
 * links (collection switcher, blog sidebar, customer-service nav). On small/
 * medium viewports it keeps the active item — the server-rendered
 * `.current-menu-item` — centered, both on initial load and whenever the page
 * changes. This module owns the scroll math so the two callers don't each
 * re-implement it:
 *
 *   - `collection.esm.js` centers on initial page load (`ep:ready` + module
 *     eval), the one case the global observer below doesn't cover.
 *   - `application.esm.js`'s `setActiveSubNavItem` (an `url.path` observer)
 *     centers on every navigation. Keying off each jump nav's own
 *     `.current-menu-item` — rather than the global `a[href]` lookup that
 *     observer uses for class management — is what makes the recenter fire
 *     reliably even when that lookup resolves to a sidebar/header link first.
 *
 * Source path: theme/js/core/jump-nav.esm.js
 * Built name : ./jump-nav.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-06-16
 * Model: Claude Opus 4.8
 */

import { appGet } from './app-get.js';

const JUMP_NAV_SELECTOR = '.js-jump-nav';

/**
 * Scroll a single jump nav so its `.current-menu-item` sits centered in the
 * viewport. No-op when the nav has no active item, isn't visible
 * (`offsetParent` is null for a `display:none` ancestor / collapsed region),
 * or is already centered.
 */
export function centerJumpNav(nav) {
  if (!nav) return;
  const current = nav.querySelector('.current-menu-item');
  if (!current) return;
  if (nav.offsetParent === null) return;
  const centeredOffset = window.innerWidth / 2 - current.offsetWidth / 2;
  const distance = current.offsetLeft - centeredOffset;
  if (nav.scrollLeft !== distance) {
    nav.scrollTo({ left: distance, behavior: 'smooth' });
  }
}

/**
 * Center every jump nav on the page. Gated to small/medium viewports — the
 * jump nav is `display:none` on large screens, where the full nav-list renders
 * in a sidebar instead. Safe to call on any page: a no-op when none exist.
 */
export function centerActiveJumpNavs() {
  const smallMedium = appGet('breakpoints.smallMedium', null);
  if (!smallMedium || smallMedium.matches !== true) return;
  document.querySelectorAll(JUMP_NAV_SELECTOR).forEach(centerJumpNav);
}
